import { cbSemanticCssVars, elevationCss } from '@creditornot/cb-ingredients';
import React from 'react';
import ReactModal from 'react-modal';
import styled, { css, keyframes } from 'styled-components';

type Position = 'center' | 'right' | 'left' | 'bottom' | 'fullscreen';

/** Describes overlay and content element references passed to onAfterOpen function */
type OnAfterOpenCallbackOptions = {
  /** overlay element reference */
  overlayEl: Element;
  /** content element reference */
  contentEl: HTMLDivElement;
};

/** Describes unction that will be run after the modal has opened */
type OnAfterOpenCallback = (obj?: OnAfterOpenCallbackOptions) => void;

type ModalProps = {
  /* Boolean indicating if the appElement should be hidden. Defaults to true. */
  ariaHideApp?: boolean | undefined;
  children?: React.ReactNode;
  className?: string;
  /** default: 340 */
  minWidth?: number;
  isOpen: boolean;
  position?: Position;
  /* Boolean indicating if pressing the esc key should close the modal */
  shouldCloseOnEsc?: boolean | undefined;
  /* Boolean indicating if the overlay should close the modal. Defaults to true. */
  shouldCloseOnOverlayClick?: boolean | undefined;
  /* Boolean indicating if the modal should be focused after render */
  shouldFocusAfterRender?: boolean | undefined;
  /* Boolean indicating if the modal should restore focus to the element that had focus prior to its display. */
  shouldReturnFocusAfterClose?: boolean | undefined;
  style?: React.CSSProperties;
  onRequestClose?: () => void;
  /* Function that will be run after the modal has opened. */
  onAfterOpen?: OnAfterOpenCallback | undefined;
  /* Function that will be run after the modal has closed. */
  onAfterClose?(): void;
};

const zoomEnter = keyframes`
  from {
    transform: scale(0.5, 0.5);
  }

  to {
    transform: scale(1, 1);
  }
`;

const zoomLeave = keyframes`
  from {
    transform: scale(1, 1);
  }

  to {
    transform: scale(0.5, 0.01);
  }
`;

const slideLeftIn = keyframes`
  from {
    transform: translateX(100%);
  }

  to {
    transform: translateX(0%) translateZ(0);
  }
`;

const slideLeftOut = keyframes`
  from {
    transform: translateX(0%);
  }

  to {
    transform: translateX(100%);
  }
`;

const slideRightIn = keyframes`
  from {
    transform: translateX(-100%);
  }

  to {
    transform: translateX(0%);
  }
`;

const slideRightOut = keyframes`
  from {
    transform: translateX(0%);
  }

  to {
    transform: translateX(-100%);
  }
`;

const slideBottomIn = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0%);
  }
`;

const slideBottomOut = keyframes`
  from {
    transform: translateY(0%);
  }
  to {
    transform: translateY(100%);
  }
`;

const StyledReactModal = styled(ReactModal)<{
  minWidth: number;
  position: Position;
  roundedCorners: boolean;
}>`
  position: relative;
  overflow-y: auto;

  ${({ position }) =>
    position === 'fullscreen' &&
    css`
      position: fixed;
      bottom: 0;
      top: 0;
      left: 0;
      right: 0;
      box-shadow: 0 0 10px 0 rgba(32 32 36 / 50%);

      &.ReactModal__Content {
        animation: ${zoomEnter} both cubic-bezier(0.4, 0, 0, 1.15);
        animation-duration: 300ms;
      }
      &.ReactModal__Content--after-open {
        opacity: 1;
      }
      &.ReactModal__Content--before-close {
        opacity: 0.01;
        transition: opacity ease-in 300ms;
        animation: ${zoomLeave} both cubic-bezier(0.4, 0, 0, 1.15);
        animation-duration: 300ms;
      }
    `}

  ${({ position }) =>
    position === 'center' &&
    css`
      transition: opacity ease-in 300ms;
      max-height: 100%;

      &.ReactModal__Content {
        animation: ${zoomEnter} both cubic-bezier(0.4, 0, 0, 1.15);
        animation-duration: 300ms;
      }
      &.ReactModal__Content--after-open {
        opacity: 1;
      }
      &.ReactModal__Content--before-close {
        opacity: 0.01;
        transition: opacity ease-in 300ms;
        animation: ${zoomLeave} both cubic-bezier(0.4, 0, 0, 1.15);
        animation-duration: 300ms;
      }
    `}

  ${({ position }) =>
    position === 'right' &&
    css`
      height: 100%;

      &.ReactModal__Content {
        animation: ${slideLeftIn} both cubic-bezier(0.345, 0.42, 0.01, 0.995);
        animation-duration: 500ms;
      }
      &.ReactModal__Content--before-close {
        animation: ${slideLeftOut} both cubic-bezier(0, 0.04, 0, 0.95);
        animation-duration: 400ms;
      }
    `}

  ${({ position }) =>
    position === 'left' &&
    css`
      height: 100%;
      box-shadow: 0 0 10px 0 rgba(32 32 36 / 50%);

      &.ReactModal__Content {
        animation: ${slideRightIn} both cubic-bezier(0.345, 0.42, 0.01, 0.995);
        animation-duration: 500ms;
      }
      &.ReactModal__Content--before-close {
        animation: ${slideRightOut} both cubic-bezier(0, 0.04, 0, 0.95);
        animation-duration: 400ms;
      }
    `}


  ${({ position }) =>
    position === 'bottom' &&
    css`
      transition: opacity ease-in 300ms;
      box-shadow: 0 0 10px 0 rgba(32 32 36 / 50%);
      width: 100%;
      bottom: 0;
      position: fixed;

      &.ReactModal__Content {
        animation: ${slideBottomIn} both cubic-bezier(0.345, 0.42, 0.01, 0.995);
        animation-duration: 500ms;
      }
      &.ReactModal__Content--before-close {
        opacity: 0.01;
        animation: ${slideBottomOut} both cubic-bezier(0, 0.04, 0, 0.95);
        animation-duration: 400ms;
      }
    `}

  min-width: ${({ minWidth }) => minWidth}px;
  border-radius: ${({ roundedCorners }) => (roundedCorners ? 8 : 0)}px;

  ${elevationCss(12)}
  box-shadow: none; /* override */
  color: ${cbSemanticCssVars.textPrimary};

  /* Remove scroll on the body when react-modal is open */
  &.ReactModal__Body--open {
    overflow-y: hidden;
  }
`;

const Overlay = styled.div<{ position: Position }>`
  outline: none;
  box-sizing: border-box;
  transform: translateX(0%); /* fixes transitions to work smoothly on Firefox */

  position: fixed;
  z-index: 5000; /* note: .leaflet-control z-index is 800, navbar 2000 */
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;

  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  transition: ease-out background-color 400ms;

  backface-visibility: hidden;

  ${({ position }) =>
    position === 'center' &&
    css`
      align-items: center;
      justify-content: center;
    `}

  ${({ position }) =>
    position === 'right' &&
    css`
      align-items: center;
      justify-content: flex-end;
    `}

  ${({ position }) =>
    position === 'left' &&
    css`
      align-items: center;
      justify-content: flex-start;
    `}

  &.ReactModal__Overlay--after-open {
    background-color: rgba(0 0 0 / 40%);
  }
  &.ReactModal__Overlay--before-close {
    background-color: rgba(0 0 0 / 0%);
  }
`;

/**
 * @deprecated use `Modal` from `@creditornot/cbt-modal` instead
 * @see https://github.com/creditornot/wolt-cookbook/tree/master/cbt-packages/modal
 */
const WoltModal = ({
  ariaHideApp = false,
  children,
  className,
  isOpen,
  minWidth = 340,
  onRequestClose,
  position = 'center',
  shouldCloseOnOverlayClick = true,
  shouldCloseOnEsc = true,
  style,
  ...rest
}: ModalProps) => (
  <StyledReactModal
    {...rest}
    ariaHideApp={ariaHideApp}
    className={className}
    closeTimeoutMS={400}
    isOpen={isOpen}
    minWidth={minWidth}
    onRequestClose={onRequestClose}
    overlayElement={({ style, ...props }, contentElement) => (
      <Overlay {...props} position={position}>
        {contentElement}
      </Overlay>
    )}
    position={position}
    roundedCorners={position === 'center'}
    shouldCloseOnEsc={shouldCloseOnEsc}
    shouldCloseOnOverlayClick={shouldCloseOnOverlayClick}
    style={{ content: style }}
  >
    {children}
  </StyledReactModal>
);

export default WoltModal;
